<template>
    <div
        class="time-picker-spinbutton form-control d-inline-flex flex-column align-items-center"
        :class="[columnClass, {disabled}]"
        role="group"
        :aria-label="label">
        <button
            type="button"
            class="btn btn-sm border-0 rounded-0 d-flex align-items-center justify-content-center w-100"
            :disabled="disabled"
            tabindex="-1"
            :aria-label="incrementLabel"
            @click="$emit('increment')">
            <SvgIcon icon="mdi-chevron-up" size="20" class="text-primary"/>
        </button>
        <div
            ref="value"
            class="flex-grow-1 d-flex align-items-center justify-content-center border-top border-bottom time-picker-value"
            role="spinbutton"
            :tabindex="disabled ? -1 : 0"
            :aria-label="label"
            :aria-valuenow="valueNow"
            :aria-valuemin="valueMin"
            :aria-valuemax="valueMax"
            :aria-valuetext="formatted"
            @keydown="onValueKeydown">
            <bdi>{{ formatted }}</bdi>
        </div>
        <button
            type="button"
            class="btn btn-sm border-0 rounded-0 d-flex align-items-center justify-content-center w-100"
            :disabled="disabled"
            tabindex="-1"
            :aria-label="decrementLabel"
            @click="$emit('decrement')">
            <SvgIcon icon="mdi-chevron-down" size="20" class="text-primary"/>
        </button>
    </div>
</template>

<script>
export default {
    name: 'TimePickerSpinColumn',
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            required: true,
        },
        formatted: {
            type: String,
            required: true,
        },
        valueNow: {
            default: null,
        },
        valueMin: {
            type: Number,
            default: 0,
        },
        valueMax: {
            type: Number,
            default: 59,
        },
        columnClass: {
            type: String,
            default: '',
        },
        labelIncrement: {
            type: String,
            default: null,
        },
        labelDecrement: {
            type: String,
            default: null,
        },
        tabFromFirst: {
            type: Boolean,
            default: false,
        },
        tabFromLast: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        incrementLabel() {
            return this.labelIncrement ?? this.$t('Increase');
        },
        decrementLabel() {
            return this.labelDecrement ?? this.$t('Decrease');
        },
    },
    methods: {
        focus() {
            if (!this.disabled && this.$refs.value) {
                this.$refs.value.focus();
            }
        },
        onValueKeydown(event) {
            if (this.disabled) {
                return;
            }
            if (event.key === 'Tab') {
                if (event.shiftKey && this.tabFromFirst) {
                    event.preventDefault();
                    this.$emit('tab-to-input');
                } else if (!event.shiftKey && this.tabFromLast) {
                    event.preventDefault();
                    this.$emit('tab-exit');
                }
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.$emit('increment');
            } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.$emit('decrement');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@common/styles/variables/_variables.scss";

.time-picker-spinbutton {
    text-align: center;
    overflow: hidden;
    background-image: none;
    padding: 0;
    height: auto;
    width: auto;

    .time-picker-value {
        font-size: inherit;
        background-color: transparent;
        width: auto;
        margin: 0 0.25rem;
        padding: 0.25rem 0;
        outline: none;
        cursor: ns-resize;

        > bdi {
            display: block;
            min-width: 2.25em;
            height: 1em * $line-height-base;
        }
    }

    .btn {
        line-height: 1;
        box-shadow: none !important;

        &:disabled {
            pointer-events: none;
        }
    }

    &.disabled {
        background-color: $gray-200;
        pointer-events: none;
    }

    &:focus-within {
        border-color: $primary;
        border-radius: 0.25rem;
        z-index: 1;

        .time-picker-value {
            border-color: $primary !important;
        }
    }
}
</style>
